<template>
  <div v-if="calendar" class="stage-timers">
    <div class="timer-row">
      <div class="timer-label">{{ $t('rounds.upload_close') }}</div>
      <div class="timer-bar" :style="segmentStyle(times.opens, times.closes)">
        <div
          class="timer-fill"
          :style="[segmentStyle(times.opens, times.closes), { width: uploadProgress + '%' }]"
        ></div>
      </div>
      <div class="timer-date-time">
        <div class="timer-date">{{ formatDateShort(times.closes) }}</div>
        <div class="timer-time">{{ formatTimeShort(times.closes) }}</div>
      </div>
    </div>
    <div class="timer-row" v-if="times.publish">
      <div class="timer-label">{{ $t('rounds.publication') }}</div>
      <div class="timer-bar" :style="segmentStyle(times.opens, times.publish)">
        <div
          class="timer-fill publish"
          :style="[segmentStyle(times.opens, times.publish), { width: publishProgress + '%' }]"
        ></div>
      </div>
      <div class="timer-date-time">
        <div class="timer-date">{{ formatDateShort(times.publish) }}</div>
        <div class="timer-time">{{ formatTimeShort(times.publish) }}</div>
      </div>
    </div>
    <div v-if="code" class="timer-code">
      {{ $t('rounds.verification_code') }}: <span class="code-pill">{{ code }}</span>
    </div>
  </div>
</template>

<script>
import { formatCompetitionTime, parseCompetitionDate, resolveCompetitionTimezone } from "@/helpers/timezoneDate";

export default {
  name: 'StageTimersBox',
  props: {
    calendar: { type: Object, default: null },
    competition: { type: Object, default: null },
    code: { type: String, default: '' },
    nowTs: { type: Number, default: () => Date.now() },
  },
  computed: {
    competitionTimezone() {
      return resolveCompetitionTimezone(this.competition);
    },
    times() {
      return this.calendarTimes(this.calendar);
    },
    uploadProgress() {
      const { opens, closes } = this.times;
      if (!closes) return 0;
      const start = opens || this.nowTs;
      const pct = this.progressBetween(start, closes);
      return pct > 0 ? pct : 1;
    },
    publishProgress() {
      const { opens, publish } = this.times;
      if (!publish) return 0;
      const start = opens || this.nowTs;
      const pct = this.progressBetween(start, publish);
      return pct > 0 ? pct : 1;
    },
  },
  methods: {
    calendarTimes(calendar) {
      if (!calendar) return { opens: null, closes: null, publish: null };
      const opens = calendar.upload_opens_at
        || calendar.upload_open_at
        || calendar.uploadOpensAt
        || calendar.uploadOpenAt
        || calendar.opens_at
        || calendar.open_at
        || calendar.opensAt
        || calendar.openAt
        || calendar.opens
        || null;
      const closes = calendar.upload_closes_at
        || calendar.upload_close_at
        || calendar.uploadClosesAt
        || calendar.uploadCloseAt
        || calendar.closes_at
        || calendar.close_at
        || calendar.closesAt
        || calendar.closeAt
        || calendar.closes
        || null;
      const publish = calendar.publish_at
        || calendar.published_at
        || calendar.publishAt
        || calendar.publishedAt
        || null;
      return { opens, closes, publish };
    },
    parseTimestamp(raw) {
      if (!raw) return NaN;
      const parsed = parseCompetitionDate(raw, this.competitionTimezone);
      return parsed instanceof Date ? parsed.getTime() : NaN;
    },
    progressBetween(startRaw, endRaw) {
      const startTs = this.parseTimestamp(startRaw);
      const endTs = this.parseTimestamp(endRaw);
      if (Number.isNaN(startTs) || Number.isNaN(endTs)) return 0;
      if (endTs <= startTs) {
        return this.nowTs >= endTs ? 100 : 0;
      }
      if (this.nowTs <= startTs) return 0;
      if (this.nowTs >= endTs) return 100;
      const ratio = (this.nowTs - startTs) / (endTs - startTs);
      return Math.max(0, Math.min(100, Math.round(ratio * 100)));
    },
    formatDateShort(raw) {
      if (!raw) return '--';
      const ts = this.parseTimestamp(raw);
      if (Number.isNaN(ts)) return '--';
      const date = new Date(ts);
      if (Number.isNaN(date.getTime())) return '--';
      try {
        return new Intl.DateTimeFormat(undefined, {
          timeZone: this.competitionTimezone || undefined,
          day: '2-digit',
          month: 'short'
        }).format(date);
      } catch (error) {
        return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
      }
    },
    formatTimeShort(raw) {
      if (!raw) return '--';
      const ts = this.parseTimestamp(raw);
      if (Number.isNaN(ts)) return '--';
      const date = new Date(ts);
      if (Number.isNaN(date.getTime())) return '--';
      return formatCompetitionTime(date, this.competitionTimezone) || '--';
    },
    segmentStyle(startRaw, endRaw) {
      const startTs = this.parseTimestamp(startRaw);
      const endTs = this.parseTimestamp(endRaw);
      if (Number.isNaN(startTs) || Number.isNaN(endTs) || endTs <= startTs) return {};
      const days = Math.max(1, Math.ceil((endTs - startTs) / (24 * 60 * 60 * 1000)));
      const step = 100 / days;
      const gap = Math.min(1.5, step / 4);
      const seg = step - gap;
      const pattern = `repeating-linear-gradient(90deg, transparent 0, transparent ${seg}%, rgba(0, 0, 0, 0.08) ${seg}%, rgba(0, 0, 0, 0.08) ${step}%)`;
      return { '--segment-pattern': pattern };
    }
  },
};
</script>

<style scoped>
.stage-timers {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  margin: 10px 0 14px;
}

.timer-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.timer-row:last-child {
  margin-bottom: 0;
}

.timer-label {
  width: 140px;
  font-weight: 600;
  font-size: 0.9rem;
  color: #374151;
}

.timer-date-time {
  min-width: 120px;
  text-align: right;
}

.timer-date {
  font-size: 0.85rem;
  color: #4b5563;
  line-height: 1.2;
}

.timer-time {
  font-size: 0.9rem;
  color: #1f2937;
  font-weight: 600;
}

.timer-bar {
  flex: 1;
  height: 10px;
  background-color: #e5e7eb;
  background-image: var(--segment-pattern, none);
  border-radius: 999px;
  overflow: hidden;
}

.timer-fill {
  height: 100%;
  background-image: linear-gradient(90deg, #3b82f6, #60a5fa), var(--segment-pattern, none);
  background-blend-mode: normal, overlay;
  transition: width 0.3s ease;
}

.timer-fill.publish {
  background-image: linear-gradient(90deg, #10b981, #34d399), var(--segment-pattern, none);
  background-blend-mode: normal, overlay;
}

.timer-code {
  margin-top: 8px;
  font-weight: 600;
  color: #1f2937;
}

.code-pill {
  display: inline-block;
  margin-left: 6px;
  padding: 4px 8px;
  background: #eef2ff;
  color: #3730a3;
  border-radius: 6px;
  font-family: monospace;
  border: 1px solid #c7d2fe;
}
</style>
